import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  TextField
} from '@mui/material'
import { DriverJurisdictionTextField, ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { InboundTripDriverInformationDto, inboundTripDriverInformationValidationSchema, InboundTripDto, PersonDto } from 'dtos'
import { Formik } from 'formik'
import { useLoadingState } from 'hooks'
import { AUTOCOMPLETE_PAGESIZE, DEBOUNCE_DELAY, Junction } from 'localConstants'
import { useState } from 'react'
import { useDebounce } from 'utils'

interface DriverInformationAddEditDialogProps {
  initialValues: InboundTripDriverInformationDto
  inboundTrip: InboundTripDto
  isLoading: boolean
  open: boolean
  onClose: () => void
  onSave: (values: InboundTripDriverInformationDto) => void
}

export default function DriverInformationAddEditDialog({
  initialValues,
  inboundTrip,
  isLoading,
  onClose,
  onSave,
  open
}: DriverInformationAddEditDialogProps) {
  const [drivers, setDrivers] = useState<PersonDto[]>([])
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingDriverPeople: false,
    isGettingPersonDriverInformation: false
  })

  const getDriverPeopleAutocomplete = useDebounce((companyGuid: string, search: string) => {
    setLoadingState('isGettingDriverPeople', true)
    controllers
      .getPeople({
        companyGuid,
        junction: Junction.AND,
        order: 'asc',
        orderBy: 'name',
        page: 0,
        pageSize: AUTOCOMPLETE_PAGESIZE,
        search,
        statuses: ['isActive', 'isDriver']
      })
      .then(response => {
        setDrivers(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingDriverPeople', false)
      })
  }, DEBOUNCE_DELAY)

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values, formikHelpers) => {
          onSave(values)
          formikHelpers.setSubmitting(false)
        }}
        validateOnBlur={true}
        validateOnChange={true}
        validationSchema={inboundTripDriverInformationValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => {
          return (
            <form onSubmit={handleSubmit}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <DialogTitle>Edit Driver Information</DialogTitle>

                  <Grid item xs={12}>
                    <LabelField label='Inbound Trip #' value={inboundTrip.inboundTripNumber} />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      autoHighlight
                      autoSelect
                      disabled={isLoading || isSubmitting}
                      disableListWrap
                      getOptionLabel={person => `${person.firstName} ${person.lastName}`}
                      noOptionsText={
                        loadingState.isGettingDriverPeople
                          ? 'Searching Drivers...'
                          : 'No Drivers. Start typing to search for Drivers...'
                      }
                      onBlur={handleBlur}
                      onChange={(_e, value) => {
                        setFieldValue('driverPerson', value)

                        if (value?.personGuid) {
                          setLoadingState('isGettingPersonDriverInformation', true)
                          controllers
                            .getPersonDriverInformation(value.personGuid)
                            .then(response => {
                              setFieldValue('driverVehiclePlate', response.value.driverVehiclePlate)
                              setFieldValue('driverVehicleState', response.value.driverVehicleState)
                              setFieldValue('driverVehicleCountry', response.value.driverVehicleCountry)
                              setFieldValue('driverTrailerPlate', response.value.driverTrailerPlate)
                              setFieldValue('driverTrailerState', response.value.driverTrailerState)
                              setFieldValue('driverTrailerCountry', response.value.driverTrailerCountry)
                              setFieldValue('driverCrewMember', response.value.driverCrewMember)
                            })
                            .finally(() => {
                              setLoadingState('isGettingPersonDriverInformation', false)
                            })
                        } else {
                          setFieldValue('driverVehiclePlate', undefined)
                          setFieldValue('driverVehicleState', undefined)
                          setFieldValue('driverVehicleCountry', undefined)
                          setFieldValue('driverTrailerPlate', undefined)
                          setFieldValue('driverTrailerState', undefined)
                          setFieldValue('driverTrailerCountry', undefined)
                          setFieldValue('driverCrewMember', undefined)
                        }
                      }}
                      onInputChange={(_e, value, _reason) => {
                        getDriverPeopleAutocomplete(inboundTrip.inboundTransporterCompany?.companyGuid, value)
                      }}
                      options={drivers.map(driver => ({
                        personGuid: driver.personGuid,
                        firstName: driver.firstName,
                        lastName: driver.lastName
                      }))}
                      renderInput={params => (
                        <TextField
                          {...params}
                          error={Boolean(touched.driverPerson && errors.driverPerson)}
                          helperText={touched.driverPerson && errors.driverPerson}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingState.isGettingDriverPeople && (
                                  <InputAdornment position='end'>
                                    <CircularProgress size={24} />
                                  </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                          label='Driver'
                        />
                      )}
                      value={
                        values.driverPerson
                          ? {
                              personGuid: values.driverPerson.personGuid,
                              firstName: values.driverPerson.firstName,
                              lastName: values.driverPerson.lastName
                            }
                          : null
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting || loadingState.isGettingPersonDriverInformation}
                      error={Boolean(touched.driverVehiclePlate && errors.driverVehiclePlate)}
                      fullWidth
                      helperText={touched.driverVehiclePlate && errors.driverVehiclePlate}
                      InputProps={{
                        endAdornment: (
                          <>
                            {loadingState.isGettingPersonDriverInformation && (
                              <InputAdornment position='end'>
                                <CircularProgress size={24} />
                              </InputAdornment>
                            )}
                          </>
                        )
                      }}
                      label='Vehicle Plate'
                      name='driverVehiclePlate'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.driverVehiclePlate || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DriverJurisdictionTextField
                      disabled={isLoading || isSubmitting || loadingState.isGettingPersonDriverInformation}
                      error={Boolean(touched.driverVehicleState && errors.driverVehicleState)}
                      fullWidth
                      helperText={touched.driverVehicleState && errors.driverVehicleState}
                      InputProps={{
                        endAdornment: (
                          <>
                            {loadingState.isGettingPersonDriverInformation && (
                              <InputAdornment position='end'>
                                <CircularProgress size={24} />
                              </InputAdornment>
                            )}
                          </>
                        )
                      }}
                      label='Vehicle State/Province'
                      name='driverVehicleState'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.driverVehicleState || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DriverJurisdictionTextField
                      disabled={isLoading || isSubmitting || loadingState.isGettingPersonDriverInformation}
                      error={Boolean(touched.driverVehicleCountry && errors.driverVehicleCountry)}
                      fullWidth
                      helperText={touched.driverVehicleCountry && errors.driverVehicleCountry}
                      InputProps={{
                        endAdornment: (
                          <>
                            {loadingState.isGettingPersonDriverInformation && (
                              <InputAdornment position='end'>
                                <CircularProgress size={24} />
                              </InputAdornment>
                            )}
                          </>
                        )
                      }}
                      label='Vehicle Country'
                      name='driverVehicleCountry'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.driverVehicleCountry || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting || loadingState.isGettingPersonDriverInformation}
                      error={Boolean(touched.driverTrailerPlate && errors.driverTrailerPlate)}
                      fullWidth
                      helperText={touched.driverTrailerPlate && errors.driverTrailerPlate}
                      InputProps={{
                        endAdornment: (
                          <>
                            {loadingState.isGettingPersonDriverInformation && (
                              <InputAdornment position='end'>
                                <CircularProgress size={24} />
                              </InputAdornment>
                            )}
                          </>
                        )
                      }}
                      label='Trailer Plate'
                      name='driverTrailerPlate'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.driverTrailerPlate || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DriverJurisdictionTextField
                      disabled={isLoading || isSubmitting || loadingState.isGettingPersonDriverInformation}
                      error={Boolean(touched.driverTrailerState && errors.driverTrailerState)}
                      fullWidth
                      helperText={touched.driverTrailerState && errors.driverTrailerState}
                      InputProps={{
                        endAdornment: (
                          <>
                            {loadingState.isGettingPersonDriverInformation && (
                              <InputAdornment position='end'>
                                <CircularProgress size={24} />
                              </InputAdornment>
                            )}
                          </>
                        )
                      }}
                      label='Trailer State/Province'
                      name='driverTrailerState'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.driverTrailerState || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DriverJurisdictionTextField
                      disabled={isLoading || isSubmitting || loadingState.isGettingPersonDriverInformation}
                      error={Boolean(touched.driverTrailerCountry && errors.driverTrailerCountry)}
                      fullWidth
                      helperText={touched.driverTrailerCountry && errors.driverTrailerCountry}
                      InputProps={{
                        endAdornment: (
                          <>
                            {loadingState.isGettingPersonDriverInformation && (
                              <InputAdornment position='end'>
                                <CircularProgress size={24} />
                              </InputAdornment>
                            )}
                          </>
                        )
                      }}
                      label='Trailer Country'
                      name='driverTrailerCountry'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.driverTrailerCountry || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      disabled={isLoading || isSubmitting || loadingState.isGettingPersonDriverInformation}
                      error={Boolean(touched.driverCrewMember && errors.driverCrewMember)}
                      fullWidth
                      helperText={touched.driverCrewMember && errors.driverCrewMember}
                      InputProps={{
                        endAdornment: (
                          <>
                            {loadingState.isGettingPersonDriverInformation && (
                              <InputAdornment position='end'>
                                <CircularProgress size={24} />
                              </InputAdornment>
                            )}
                          </>
                        )
                      }}
                      label='Crew Member'
                      name='driverCrewMember'
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.driverCrewMember || ''}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || Driver is a dropdown and is populated based on the selected Inbound Transporter Company and shows the
                      name of any People associated with that company that have the “Driver” toggle on under People/Person
                      Details/Person Information (dialog). || The Driver Information card fields are text fields that are
                      populated based on the selected Driver. These fields are read from the Driver Information card under
                      Person Details: Vehicle Plate, Vehicle State/Province, Vehicle Country, Trailer Plate, Trailer
                      State/Province, Trailer Country, and Crew. || The Driver Information card fields are text fields that are
                      editable by the user adding or editing the Trip. The information entered here does not update or overwrite
                      the data stored on the Driver Information card, but whatever is in these fields will apply to this
                      specific Inbound Trip. None of these fields are required. ||
                    </ExplanationAccordion>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button color='inherit' disabled={isLoading || isSubmitting} onClick={onClose} variant='text'>
                  CANCEL
                </Button>

                <LoadingButton
                  disabled={inboundTrip.isLocked}
                  loading={isLoading || isSubmitting}
                  type='submit'
                  variant='contained'
                >
                  SAVE
                </LoadingButton>
              </DialogActions>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
